/**
 * Authentication Engine - Provider-agnostic auth system.
 *
 * Decoupled from any specific provider (Supabase, Firebase, custom).
 * Supports email/password, magic link, and OAuth abstraction.
 * Handles session persistence, refresh, rotation, and device awareness.
 *
 * Auth is replaceable without rewriting the app.
 *
 * Extends: authSecurity (config/scoring), secretVault (key storage)
 */

import { evaluateAuthSecurity, defaultAuthSecurityConfig, type AuthSecurityConfig } from "@/security/authSecurity";
import { eventBus } from "@/runtime/eventBus";
import { logger } from "@/system/logger";

// ─── Core Types ───

export interface AuthUser {
  id: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  emailVerified: boolean;
  createdAt: number;
  lastLoginAt: number;
  metadata: Record<string, unknown>;
}

export interface AuthSession {
  id: string;
  userId: string;
  /** Access token - never stored in plaintext at rest */
  accessToken: string;
  /** Refresh token for rotation */
  refreshToken: string;
  expiresAt: number;
  device: DeviceInfo;
  createdAt: number;
  lastActivityAt: number;
}

export interface DeviceInfo {
  id: string;
  userAgent: string;
  platform: string;
  isTrusted: boolean;
  firstSeenAt: number;
}

export type AuthProvider = "email" | "magic_link" | "google" | "github" | "apple" | "custom";

export interface AuthCredentials {
  provider: AuthProvider;
  email?: string;
  password?: string;
  token?: string;
  oauthCode?: string;
}

export interface AuthResult {
  success: boolean;
  user?: AuthUser;
  session?: AuthSession;
  error?: string;
  requiresVerification?: boolean;
}

// ─── Provider Interface (replaceable) ───

export interface AuthProviderAdapter {
  id: AuthProvider;
  signUp(credentials: AuthCredentials): Promise<AuthResult>;
  signIn(credentials: AuthCredentials): Promise<AuthResult>;
  signOut(sessionId: string): Promise<void>;
  refreshSession(refreshToken: string): Promise<AuthResult>;
  resetPassword(email: string): Promise<{ success: boolean; error?: string }>;
  verifyEmail(token: string): Promise<{ success: boolean; error?: string }>;
  getUser(userId: string): Promise<AuthUser | null>;
}

// ─── Auth Engine ───

class AuthEngine {
  private currentUser: AuthUser | null = null;
  private currentSession: AuthSession | null = null;
  private memorySession: AuthSession | null = null;
  private provider: AuthProviderAdapter | null = null;
  private securityConfig: AuthSecurityConfig = { ...defaultAuthSecurityConfig };
  private loginAttempts = new Map<string, { count: number; lockedUntil: number }>();
  private stateListeners = new Set<(user: AuthUser | null) => void>();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  // True until the first auth state event (SIGNED_IN or SIGNED_OUT) resolves.
  private authResolved = false;

  /**
   * Register an auth provider adapter. Can be swapped at runtime.
   */
  setProvider(provider: AuthProviderAdapter): void {
    this.provider = provider;
    logger.info("Auth provider registered", { providerId: provider.id });
  }

  /**
   * Configure security settings.
   */
  configure(config: Partial<AuthSecurityConfig>): void {
    this.securityConfig = { ...this.securityConfig, ...config };
  }

  /**
   * Sign up a new user.
   */
  async signUp(credentials: AuthCredentials): Promise<AuthResult> {
    if (!this.provider) return { success: false, error: "No auth provider configured" };

    // Validate password policy
    if (credentials.provider === "email" && credentials.password) {
      const policyError = this.validatePasswordPolicy(credentials.password);
      if (policyError) return { success: false, error: policyError };
    }

    try {
      const result = await this.provider.signUp(credentials);
      if (result.success && result.user) {
        if (!this.securityConfig.emailVerificationRequired) {
          this.setAuthState(result.user, result.session ?? null);
        }
        logger.info("User signed up", { userId: result.user.id });
      }
      return result;
    } catch (err) {
      const error = err instanceof Error ? err.message : "Sign up failed";
      logger.error("Sign up error", { error });
      return { success: false, error };
    }
  }

  /**
   * Sign in an existing user.
   */
  async signIn(credentials: AuthCredentials): Promise<AuthResult> {
    if (!this.provider) return { success: false, error: "No auth provider configured" };

    const email = credentials.email ?? "";

    // Check lockout
    const attempts = this.loginAttempts.get(email);
    if (attempts && attempts.lockedUntil > Date.now()) {
      const remainingMs = attempts.lockedUntil - Date.now();
      return { success: false, error: `Account locked. Try again in ${Math.ceil(remainingMs / 60000)} minutes.` };
    }

    try {
      const result = await this.provider.signIn(credentials);
      if (result.success && result.user) {
        this.loginAttempts.delete(email);
        this.setAuthState(result.user, result.session ?? null);
        logger.info("User signed in", { userId: result.user.id, provider: credentials.provider });
      } else {
        this.recordFailedAttempt(email);
      }
      return result;
    } catch (err) {
      this.recordFailedAttempt(email);
      const error = err instanceof Error ? err.message : "Sign in failed";
      return { success: false, error };
    }
  }

  /**
   * Sign out current user.
   */
  async signOut(): Promise<void> {
    if (this.currentSession && this.provider) {
      try {
        await this.provider.signOut(this.currentSession.id);
      } catch { /* proceed with local cleanup */ }
    }
    this.clearAuthState();
    logger.info("User signed out");
  }

  /**
   * Restore session from secure storage on app boot.
   */
  async restoreSession(): Promise<AuthResult> {
    const stored = this.loadStoredSession();
    if (!stored) return { success: false, error: "No stored session" };

    if (stored.expiresAt <= Date.now()) {
      // Attempt refresh
      if (this.provider) {
        try {
          const result = await this.provider.refreshSession(stored.refreshToken);
          if (result.success && result.user) {
            this.setAuthState(result.user, result.session ?? null);
            return result;
          }
        } catch { /* fall through */ }
      }
      this.clearAuthState();
      return { success: false, error: "Session expired" };
    }

    // Session still valid - restore user
    if (this.provider) {
      try {
        const user = await this.provider.getUser(stored.userId);
        if (user) {
          this.setAuthState(user, stored);
          return { success: true, user, session: stored };
        }
      } catch { /* fall through */ }
    }

    return { success: false, error: "Failed to restore user" };
  }

  /**
   * Subscribe to auth state changes. Returns unsubscribe function.
   */
  onAuthStateChange(listener: (user: AuthUser | null) => void): () => void {
    this.stateListeners.add(listener);
    // Emit current state immediately
    listener(this.currentUser);
    return () => this.stateListeners.delete(listener);
  }

  /**
   * Get current authenticated user.
   */
  getUser(): AuthUser | null {
    return this.currentUser;
  }

  /**
   * Get current session.
   */
  getSession(): AuthSession | null {
    return this.currentSession;
  }

  /**
   * Check if user is authenticated.
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null && this.currentSession !== null && this.currentSession.expiresAt > Date.now();
  }

  /**
   * True until the first auth state has been resolved (SIGNED_IN or SIGNED_OUT).
   * Guards prevent flashing a login form before the session is known.
   */
  isAuthLoading(): boolean {
    return !this.authResolved;
  }

  /**
   * Get security score for current configuration.
   */
  getSecurityScore(): { score: number; recommendations: string[] } {
    return evaluateAuthSecurity(this.securityConfig);
  }

  /**
   * Inject an externally-obtained session (e.g. from Supabase onAuthStateChange).
   * Use this when the provider drives auth state changes directly (OAuth redirects,
   * multi-tab sign-in, token refresh) rather than through signIn().
   */
  injectSession(user: AuthUser, session: AuthSession): void {
    this.setAuthState(user, session);
  }

  /**
   * Clear auth state from an external signal (e.g. Supabase SIGNED_OUT event
   * triggered in another tab or by token revocation).
   */
  clearSession(): void {
    this.clearAuthState();
  }

  /**
   * Get device info for current session.
   */
  getDeviceInfo(): DeviceInfo {
    return {
      id: this.generateDeviceId(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      isTrusted: false,
      firstSeenAt: Date.now(),
    };
  }

  // ─── Private ───

  private setAuthState(user: AuthUser, session: AuthSession | null): void {
    this.authResolved = true;
    this.currentUser = user;
    this.currentSession = session;

    if (session) {
      this.storeSession(session);
      this.scheduleRefresh(session);
    }

    for (const listener of this.stateListeners) {
      try { listener(user); } catch { /* safe */ }
    }
  }

  private clearAuthState(): void {
    this.authResolved = true;
    this.currentUser = null;
    this.currentSession = null;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.removeStoredSession();

    for (const listener of this.stateListeners) {
      try { listener(null); } catch { /* safe */ }
    }
  }

  private scheduleRefresh(session: AuthSession): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const refreshIn = Math.max(0, session.expiresAt - Date.now() - 60000); // Refresh 1 min before expiry
    this.refreshTimer = setTimeout(async () => {
      if (this.provider && this.currentSession) {
        try {
          const result = await this.provider.refreshSession(this.currentSession.refreshToken);
          if (result.success && result.user && result.session) {
            this.setAuthState(result.user, result.session);
          } else {
            this.clearAuthState();
          }
        } catch {
          this.clearAuthState();
        }
      }
    }, refreshIn);
  }

  private validatePasswordPolicy(password: string): string | null {
    const p = this.securityConfig.passwordPolicy;
    if (password.length < p.minLength) return `Password must be at least ${p.minLength} characters`;
    if (p.requireUppercase && !/[A-Z]/.test(password)) return "Password must contain an uppercase letter";
    if (p.requireNumber && !/[0-9]/.test(password)) return "Password must contain a number";
    if (p.requireSymbol && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) return "Password must contain a symbol";
    return null;
  }

  private recordFailedAttempt(email: string): void {
    const attempts = this.loginAttempts.get(email) ?? { count: 0, lockedUntil: 0 };
    attempts.count++;
    if (attempts.count >= this.securityConfig.maxLoginAttempts) {
      attempts.lockedUntil = Date.now() + this.securityConfig.lockoutDurationMinutes * 60000;
      attempts.count = 0;
    }
    this.loginAttempts.set(email, attempts);
  }

  private storeSession(session: AuthSession): void {
    // Tokens kept in memory only - never written to sessionStorage or localStorage.
    // Server-side httpOnly cookie (nuvra_rt) handles cross-page-load token refresh.
    this.memorySession = session;
  }

  private loadStoredSession(): AuthSession | null {
    return this.memorySession;
  }

  private removeStoredSession(): void {
    this.memorySession = null;
  }

  private generateDeviceId(): string {
    const stored = localStorage.getItem("nuvra-device-id");
    if (stored) return stored;
    const id = `dev_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("nuvra-device-id", id);
    return id;
  }
}

export const authEngine = new AuthEngine();

// ─── Default Local Provider (for development/local-first) ───

export class LocalAuthProvider implements AuthProviderAdapter {
  id: AuthProvider = "email";
  private users = new Map<string, AuthUser & { passwordHash: string }>();

  async signUp(credentials: AuthCredentials): Promise<AuthResult> {
    if (!credentials.email || !credentials.password) return { success: false, error: "Email and password required" };
    if (this.users.has(credentials.email)) return { success: false, error: "User already exists" };

    const user: AuthUser = {
      id: `user_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      email: credentials.email,
      emailVerified: false,
      createdAt: Date.now(),
      lastLoginAt: Date.now(),
      metadata: {},
    };
    this.users.set(credentials.email, { ...user, passwordHash: this.simpleHash(credentials.password) });

    const session = this.createSession(user.id);
    return { success: true, user, session };
  }

  async signIn(credentials: AuthCredentials): Promise<AuthResult> {
    if (!credentials.email || !credentials.password) return { success: false, error: "Email and password required" };
    const stored = this.users.get(credentials.email);
    if (!stored || stored.passwordHash !== this.simpleHash(credentials.password)) {
      return { success: false, error: "Invalid credentials" };
    }

    stored.lastLoginAt = Date.now();
    const { passwordHash, ...user } = stored;
    const session = this.createSession(user.id);
    return { success: true, user, session };
  }

  async signOut(): Promise<void> { /* local cleanup */ }

  async refreshSession(refreshToken: string): Promise<AuthResult> {
    return { success: false, error: "Local provider does not support refresh" };
  }

  async resetPassword(_email: string): Promise<{ success: boolean; error?: string }> {
    return { success: false, error: "LocalAuthProvider does not support password reset - configure a Supabase auth provider" };
  }

  async verifyEmail(_token: string): Promise<{ success: boolean; error?: string }> {
    return { success: false, error: "LocalAuthProvider does not support email verification - configure a Supabase auth provider" };
  }

  async getUser(userId: string): Promise<AuthUser | null> {
    for (const stored of this.users.values()) {
      if (stored.id === userId) {
        const { passwordHash, ...user } = stored;
        return user;
      }
    }
    return null;
  }

  private createSession(userId: string): AuthSession {
    return {
      id: `sess_${Date.now()}`,
      userId,
      accessToken: `at_${Math.random().toString(36).slice(2)}`,
      refreshToken: `rt_${Math.random().toString(36).slice(2)}`,
      expiresAt: Date.now() + 3600000,
      device: { id: "local", userAgent: navigator.userAgent, platform: navigator.platform, isTrusted: true, firstSeenAt: Date.now() },
      createdAt: Date.now(),
      lastActivityAt: Date.now(),
    };
  }

  private simpleHash(input: string): string {
    let h = 0;
    for (let i = 0; i < input.length; i++) { h = ((h << 5) - h) + input.charCodeAt(i); h |= 0; }
    return Math.abs(h).toString(36);
  }
}
